import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import {
  AssetClassification,
  AssetFeature,
  AssetFeatureCollection,
  AssetLayer,
  AssetManagementService,
  AssetSearchParams,
  AssetStatus,
  AssetVersion,
  AuditEntry,
  CreateAssetRequest,
  CreateLayerRequest,
  DashboardStats,
  ErpIntegration,
  ErpSyncRun,
} from '../../../shared/services/asset-management.service';

interface SnackMessage {
  kind: 'info' | 'success' | 'error';
  text: string;
}

@Component({
  selector: 'app-asset-management',
  templateUrl: './asset-management.component.html',
  styleUrls: ['./asset-management.component.scss'],
})
export class AssetManagementComponent implements OnInit, OnDestroy {
  // ----- state -----
  loadingDashboard = false;
  loadingLayers = false;
  loadingAssets = false;
  loadingAudit = false;
  saving = false;
  seedingDemo = false;

  health: { ok: boolean; postgis: boolean; message: string } | null = null;
  stats: DashboardStats | null = null;
  layers: AssetLayer[] = [];
  assets: AssetFeature[] = [];
  audit: AuditEntry[] = [];

  selectedAsset: AssetFeature | null = null;
  selectedLayer: AssetLayer | null = null;
  totalCount = 0;

  // Version history modal
  showVersionsModal = false;
  versionsAsset: AssetFeature | null = null;
  versions: AssetVersion[] = [];
  loadingVersions = false;
  pageIndex = 0;
  pageSize = 25;

  searchForm!: FormGroup;
  layerForm!: FormGroup;
  assetForm!: FormGroup;

  showLayerModal = false;
  showAssetModal = false;
  showImportModal = false;
  importLayerId: number | null = null;
  importPayload = '';
  importFormat: 'geojson' | 'kml' = 'geojson';
  importResult: {
    succeeded: number;
    failed: number;
    total: number;
    errors?: Array<{ message: string }>;
  } | null = null;

  erpForm!: FormGroup;
  erpIntegrations: ErpIntegration[] = [];
  erpSyncRuns: ErpSyncRun[] = [];
  loadingErp = false;
  syncingErp = false;

  snack: SnackMessage | null = null;

  readonly classifications: AssetClassification[] = ['underground', 'overground', 'crossover'];
  readonly statuses: AssetStatus[] = [
    'planned',
    'under_construction',
    'active',
    'inactive',
    'decommissioned',
    'abandoned',
  ];
  readonly geometryTypes = [
    'POINT',
    'LINESTRING',
    'POLYGON',
    'MULTIPOINT',
    'MULTILINESTRING',
    'MULTIPOLYGON',
  ];
  readonly assetCategories = [
    'pipeline',
    'cable',
    'sewer',
    'pole',
    'transformer',
    'road',
    'building',
    'sensor',
    'manhole',
    'valve',
    'custom',
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private api: AssetManagementService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      search: [''],
      layerId: [null],
      classification: [null],
      assetCategory: [null],
      status: [null],
      includeDeleted: [false],
    });

    this.layerForm = this.fb.group({
      code: [''],
      name: [''],
      description: [''],
      classification: ['overground' as AssetClassification],
      asset_category: ['custom'],
      geometry_type: ['POINT'],
      srid: [4326],
    });

    this.assetForm = this.fb.group({
      layer_id: [null],
      asset_code: [''],
      name: [''],
      description: [''],
      classification: ['overground' as AssetClassification],
      asset_category: ['custom'],
      status: ['active' as AssetStatus],
      depth_m: [null],
      elevation_m: [null],
      lng: [77.5946],
      lat: [12.9716],
    });

    this.erpForm = this.fb.group({
      provider: ['sap'],
      name: ['SAP-Primary'],
      instance_key: ['default'],
      default_layer_code: ['machines'],
      is_enabled: [true],
      dry_run: [false],
      erp_payload: ['[]'],
    });

    this.searchForm.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadAssets();
      });

    this.bootstrap();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----- bootstrap -----
  private bootstrap(): void {
    this.api.health().subscribe({
      next: (h) => {
        this.health = h;
        this.loadDashboard();
        this.loadLayers();
        this.loadAssets();
        this.loadAudit();
        this.loadErpIntegrations();
        this.loadErpSyncRuns();
      },
      error: (err) => this.handleError('Health check failed', err),
    });
  }

  loadErpIntegrations(): void {
    this.loadingErp = true;
    this.api.listErpIntegrations().subscribe({
      next: (resp) => {
        this.erpIntegrations = resp.data || [];
        this.loadingErp = false;
      },
      error: (err) => {
        this.loadingErp = false;
        this.handleError('Failed to load ERP integrations', err);
      },
    });
  }

  saveErpIntegration(): void {
    const v = this.erpForm.value;
    if (!v.provider || !v.name) {
      this.notify('error', 'Provider and connector name are required');
      return;
    }
    this.saving = true;
    this.api
      .saveErpIntegration({
        provider: String(v.provider).trim().toLowerCase(),
        name: v.name,
        instance_key: v.instance_key || undefined,
        is_enabled: !!v.is_enabled,
        mapping_config: {
          default_layer_code: v.default_layer_code || undefined,
        },
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.notify('success', 'ERP integration saved');
          this.loadErpIntegrations();
        },
        error: (err) => {
          this.saving = false;
          this.handleError('Failed to save ERP integration', err);
        },
      });
  }

  loadErpSyncRuns(): void {
    this.api.listErpSyncRuns({ limit: 10 }).subscribe({
      next: (resp) => {
        this.erpSyncRuns = resp.data || [];
      },
      error: (err) => this.handleError('Failed to load ERP sync runs', err),
    });
  }

  runErpSync(): void {
    let records: any[] = [];
    try {
      records = JSON.parse(this.erpForm.value.erp_payload || '[]');
    } catch {
      this.notify('error', 'Sync payload must be valid JSON array');
      return;
    }
    if (!Array.isArray(records) || records.length === 0) {
      this.notify('error', 'Provide at least one ERP record');
      return;
    }
    const v = this.erpForm.value;
    this.syncingErp = true;
    const integration = this.erpIntegrations.find(
      (x) => x.provider === String(v.provider).toLowerCase()
    );
    this.api
      .runErpSync({
        integration_id: integration?.id,
        provider: String(v.provider).toLowerCase(),
        dry_run: !!v.dry_run,
        mode: 'manual',
        records,
      })
      .subscribe({
        next: (resp) => {
          this.syncingErp = false;
          const r = resp.data;
          this.notify(
            r.failed > 0 ? 'info' : 'success',
            `ERP sync: ${r.created} created, ${r.updated} updated, ${r.failed} failed`
          );
          this.loadAssets();
          this.loadDashboard();
          this.loadAudit();
          this.loadErpSyncRuns();
          this.loadErpIntegrations();
        },
        error: (err) => {
          this.syncingErp = false;
          this.handleError('ERP sync failed', err);
        },
      });
  }

  // ----- dashboard -----
  loadDemoData(): void {
    this.seedingDemo = true;
    this.api.seedDemo(true).subscribe({
      next: (res) => {
        this.seedingDemo = false;
        const n = res.data?.assets_created ?? 0;
        this.notify('success', `Demo loaded: ${n} assets on ${res.data?.corridor || 'NH-DEMO-48'}`);
        this.loadDashboard();
        this.loadLayers();
        this.loadAssets();
        this.loadAudit();
      },
      error: (err) => {
        this.seedingDemo = false;
        this.handleError('Failed to load demo data', err);
      },
    });
  }

  loadDashboard(): void {
    this.loadingDashboard = true;
    this.api.dashboard().subscribe({
      next: (resp) => {
        this.stats = resp.data;
        this.loadingDashboard = false;
      },
      error: (err) => {
        this.loadingDashboard = false;
        this.handleError('Failed to load dashboard', err);
      },
    });
  }

  // ----- layers -----
  loadLayers(): void {
    this.loadingLayers = true;
    this.api.listLayers(false).subscribe({
      next: (resp) => {
        this.layers = resp.data || [];
        this.loadingLayers = false;
      },
      error: (err) => {
        this.loadingLayers = false;
        this.handleError('Failed to load layers', err);
      },
    });
  }

  openLayerModal(layer?: AssetLayer): void {
    this.selectedLayer = layer || null;
    if (layer) {
      this.layerForm.reset({
        code: layer.code,
        name: layer.name,
        description: layer.description || '',
        classification: layer.classification,
        asset_category: layer.asset_category,
        geometry_type: layer.geometry_type,
        srid: layer.srid,
      });
    } else {
      this.layerForm.reset({
        code: '',
        name: '',
        description: '',
        classification: 'overground',
        asset_category: 'custom',
        geometry_type: 'POINT',
        srid: 4326,
      });
    }
    this.showLayerModal = true;
  }

  closeLayerModal(): void {
    this.showLayerModal = false;
    this.selectedLayer = null;
  }

  saveLayer(): void {
    const v = this.layerForm.value as CreateLayerRequest;
    if (!v.code || !v.name) {
      this.notify('error', 'Layer code and name are required');
      return;
    }
    this.saving = true;
    const obs = this.selectedLayer
      ? this.api.updateLayer(this.selectedLayer.id, {
          name: v.name,
          description: v.description,
          classification: v.classification,
          asset_category: v.asset_category,
          geometry_type: v.geometry_type,
        })
      : this.api.createLayer(v);
    obs.subscribe({
      next: () => {
        this.saving = false;
        this.showLayerModal = false;
        this.notify('success', this.selectedLayer ? 'Layer updated' : 'Layer created');
        this.selectedLayer = null;
        this.loadLayers();
        this.loadDashboard();
      },
      error: (err) => {
        this.saving = false;
        this.handleError('Failed to save layer', err);
      },
    });
  }

  deleteLayer(layer: AssetLayer): void {
    if (!confirm(`Deactivate layer "${layer.name}"?`)) return;
    this.api.deleteLayer(layer.id).subscribe({
      next: () => {
        this.notify('success', 'Layer deactivated');
        this.loadLayers();
        this.loadDashboard();
      },
      error: (err) => this.handleError('Failed to delete layer', err),
    });
  }

  // ----- assets -----
  loadAssets(): void {
    this.loadingAssets = true;
    const v = this.searchForm.value;
    const filters: AssetSearchParams = {
      search: v.search || undefined,
      layerId: v.layerId || undefined,
      classification: v.classification || undefined,
      assetCategory: v.assetCategory || undefined,
      status: v.status || undefined,
      includeDeleted: v.includeDeleted || undefined,
      limit: this.pageSize,
      offset: this.pageIndex * this.pageSize,
    };
    this.api.searchAssets(filters).subscribe({
      next: (resp: AssetFeatureCollection) => {
        this.assets = resp.features || [];
        this.totalCount = resp.count || 0;
        this.loadingAssets = false;
      },
      error: (err) => {
        this.loadingAssets = false;
        this.handleError('Failed to load assets', err);
      },
    });
  }

  changePage(delta: number): void {
    const next = this.pageIndex + delta;
    if (next < 0) return;
    this.pageIndex = next;
    this.loadAssets();
  }

  openAssetModal(asset?: AssetFeature): void {
    this.selectedAsset = asset || null;
    if (asset) {
      const c = asset.properties.centroid || [77.5946, 12.9716];
      this.assetForm.patchValue({
        layer_id: asset.properties.layer_id,
        asset_code: asset.properties.asset_code || '',
        name: asset.properties.name || '',
        description: asset.properties.description || '',
        classification: asset.properties.classification,
        asset_category: asset.properties.asset_category,
        status: asset.properties.status,
        depth_m: asset.properties.depth_m,
        elevation_m: asset.properties.elevation_m,
        lng: c[0],
        lat: c[1],
      });
    } else {
      this.assetForm.reset({
        layer_id: this.layers[0]?.id || null,
        classification: 'overground',
        asset_category: 'custom',
        status: 'active',
        lng: 77.5946,
        lat: 12.9716,
        depth_m: null,
        elevation_m: null,
      });
    }
    this.showAssetModal = true;
  }

  closeAssetModal(): void {
    this.showAssetModal = false;
    this.selectedAsset = null;
  }

  saveAsset(): void {
    const v = this.assetForm.value;
    if (!v.layer_id) {
      this.notify('error', 'Select a layer first');
      return;
    }
    const lng = Number(v.lng);
    const lat = Number(v.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      this.notify('error', 'Provide valid lng/lat');
      return;
    }
    const payload: CreateAssetRequest = {
      layer_id: Number(v.layer_id),
      asset_code: v.asset_code || undefined,
      name: v.name || undefined,
      description: v.description || undefined,
      classification: v.classification,
      asset_category: v.asset_category,
      status: v.status,
      depth_m: v.depth_m != null && v.depth_m !== '' ? Number(v.depth_m) : null,
      elevation_m:
        v.elevation_m != null && v.elevation_m !== '' ? Number(v.elevation_m) : null,
      geometry: {
        type: 'Point',
        coordinates: [lng, lat],
      },
      srid: 4326,
    };

    this.saving = true;
    const obs = this.selectedAsset
      ? this.api.updateAsset(this.selectedAsset.id, payload)
      : this.api.createAsset(payload);
    obs.subscribe({
      next: () => {
        this.saving = false;
        this.showAssetModal = false;
        this.selectedAsset = null;
        this.notify('success', 'Asset saved');
        this.loadAssets();
        this.loadDashboard();
        this.loadAudit();
      },
      error: (err) => {
        this.saving = false;
        this.handleError('Failed to save asset', err);
      },
    });
  }

  deleteAsset(asset: AssetFeature): void {
    if (!confirm(`Delete asset ${asset.properties.asset_code || asset.id}?`)) return;
    this.api.deleteAsset(asset.id).subscribe({
      next: () => {
        this.notify('success', 'Asset deleted (soft delete)');
        this.loadAssets();
        this.loadDashboard();
        this.loadAudit();
      },
      error: (err) => this.handleError('Failed to delete asset', err),
    });
  }

  restoreAsset(asset: AssetFeature): void {
    if (!confirm(`Restore asset ${asset.properties.asset_code || asset.id}?`)) return;
    this.api.restoreAsset(asset.id).subscribe({
      next: () => {
        this.notify('success', 'Asset restored');
        this.loadAssets();
        this.loadDashboard();
        this.loadAudit();
      },
      error: (err) => this.handleError('Failed to restore asset', err),
    });
  }

  // ----- version history -----
  openVersionsModal(asset: AssetFeature): void {
    this.versionsAsset = asset;
    this.versions = [];
    this.showVersionsModal = true;
    this.loadingVersions = true;
    this.api.listVersions(asset.id).subscribe({
      next: (resp) => {
        this.versions = resp.data || [];
        this.loadingVersions = false;
      },
      error: (err) => {
        this.loadingVersions = false;
        this.handleError('Failed to load version history', err);
      },
    });
  }

  closeVersionsModal(): void {
    this.showVersionsModal = false;
    this.versionsAsset = null;
    this.versions = [];
  }

  rollbackAsset(asset: AssetFeature, version: number): void {
    if (!confirm(`Roll asset ${asset.id} back to version ${version}?`)) return;
    this.api.rollbackVersion(asset.id, version).subscribe({
      next: () => {
        this.notify('success', `Rolled back to v${version}`);
        this.loadAssets();
        this.loadAudit();
        if (this.showVersionsModal && this.versionsAsset?.id === asset.id) {
          this.openVersionsModal(asset);
        }
      },
      error: (err) => this.handleError('Rollback failed', err),
    });
  }

  // ----- audit -----
  loadAudit(): void {
    this.loadingAudit = true;
    this.api.listAudit({ limit: 25 }).subscribe({
      next: (resp) => {
        this.audit = resp.data || [];
        this.loadingAudit = false;
      },
      error: (err) => {
        this.loadingAudit = false;
        this.handleError('Failed to load audit log', err);
      },
    });
  }

  // ----- import / export -----
  openImportModal(): void {
    this.importLayerId = this.layers[0]?.id || null;
    this.importFormat = 'geojson';
    this.importPayload = '';
    this.importResult = null;
    this.showImportModal = true;
  }

  closeImportModal(): void {
    this.showImportModal = false;
  }

  onImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.importPayload = String(reader.result || '');
      const lower = file.name.toLowerCase();
      if (lower.endsWith('.kml')) this.importFormat = 'kml';
      else this.importFormat = 'geojson';
    };
    reader.readAsText(file);
  }

  runImport(): void {
    if (!this.importLayerId || !this.importPayload) {
      this.notify('error', 'Select a layer and provide payload');
      return;
    }
    this.saving = true;
    const layer = Number(this.importLayerId);
    const obs =
      this.importFormat === 'kml'
        ? this.api.importKML(layer, this.importPayload)
        : this.api.importGeoJSON(layer, JSON.parse(this.importPayload));
    obs.subscribe({
      next: (resp) => {
        this.saving = false;
        this.importResult = {
          total: resp.total,
          succeeded: resp.succeeded,
          failed: resp.failed,
          errors: (resp as any).errors || [],
        };
        this.notify(
          resp.failed === 0 ? 'success' : 'info',
          `Import done: ${resp.succeeded}/${resp.total} succeeded`
        );
        this.loadAssets();
        this.loadDashboard();
      },
      error: (err) => {
        this.saving = false;
        this.handleError('Import failed', err);
      },
    });
  }

  exportLayer(format: 'geojson' | 'kml'): void {
    const v = this.searchForm.value;
    this.api.exportLayer(format, v.layerId || undefined).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `assets-${new Date().toISOString().slice(0, 10)}.${
          format === 'geojson' ? 'geojson' : 'kml'
        }`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => this.handleError('Export failed', err),
    });
  }

  // ----- map editor handoff -----
  /**
   * Open the GIS map editor (`gisavimvp`) wired to the asset-management
   * bridge. The query string carries the editor's intent so the bridge can
   * preload the right layer/asset:
   *  - `mode=create` → opens with the save panel pre-populated
   *  - `mode=edit&assetId=…` → focuses the asset and exposes geometry update
   *  - default → asset-management context active in "view" mode
   */
  openMapEditor(asset?: AssetFeature): void {
    if (asset) {
      this.router.navigate(['/home/geospatial'], {
        queryParams: {
          mode: 'edit',
          assetId: asset.id,
          layerId: asset.properties.layer_id,
          classification: asset.properties.classification,
          category: asset.properties.asset_category,
        },
      });
    } else {
      this.router.navigate(['/home/geospatial']);
    }
  }

  /** Open the editor in "create" mode for the supplied layer (optional). */
  openMapEditorForCreate(layerId?: number | null): void {
    const queryParams: Record<string, string | number> = { mode: 'create' };
    if (layerId) {
      queryParams['layerId'] = layerId;
      const layer = this.layers.find((l) => l.id === layerId);
      if (layer) {
        queryParams['classification'] = layer.classification;
        queryParams['category'] = layer.asset_category;
      }
    }
    this.router.navigate(['/home/geospatial'], { queryParams });
  }

  /** Explicit "edit geometry" action — same as openMapEditor() with an asset
   *  but kept as a dedicated handler so the UI text/intent is unambiguous. */
  editAssetGeometryOnMap(asset: AssetFeature): void {
    this.openMapEditor(asset);
  }

  // ----- helpers -----
  classificationCount(key: string): number {
    return this.stats?.by_classification?.[key] || 0;
  }

  statusCount(key: string): number {
    return this.stats?.by_status?.[key] || 0;
  }

  layerName(id: number | null | undefined): string {
    if (!id) return '—';
    const l = this.layers.find((x) => x.id === id);
    return l ? l.name : `#${id}`;
  }

  trackById<T extends { id: number }>(_: number, item: T): number {
    return item.id;
  }

  private notify(kind: SnackMessage['kind'], text: string): void {
    this.snack = { kind, text };
    setTimeout(() => (this.snack = null) as any, 4000);
  }

  private handleError(prefix: string, err: any): void {
    const detail = err?.error?.error || err?.error?.message || err?.message || 'unknown error';
    this.notify('error', `${prefix}: ${detail}`);
    console.error('[asset-management]', prefix, err);
  }
}
